import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { motion, useAnimation, useScroll, useTransform, useInView } from 'framer-motion';
import Header from '../components/Header';
import Footer from '../components/Footer';
import TractionSection from '../components/TractionSection';
import FluxCardHero from '../components/ui/flux-card-hero';
import { CarouselStacked } from '../components/ui/carousel-07';
import HowItWorks from '../components/how-it-works';
import Integrations from '../components/integrations';
import ScrollCard from '../components/ui/scroll-card';
import WhyChooseUs from '../components/ui/why-choose-us';
import { Hero10 } from '../components/ui/hero-10';
import { Logos3 } from '../components/ui/logos-3';
import heroImage1 from '../assets/hero-collab-1.png';
import heroImage2 from '../assets/hero-collab-2.png';
import heroImage3 from '../assets/hero-collab-3.png';
import { FAQScrollAccordion } from '../components/ui/faq-scroll-accordion';
import userService from '../services/userService';
import SEO from '../components/SEO';

// Icons
import {
  Star,
  DollarSign,
  Users,
  MessagesSquare,
  Hash,
  Camera,
  ShoppingBag,
  Smartphone,
  Calendar,
  Tag,
  Check,
  Zap
} from 'lucide-react';

const Home = () => {
  const { currentUser } = useAuth();
  const { scrollY } = useScroll();
  const [profilePhotos, setProfilePhotos] = useState([]);
  
  // Background animation values
  const bgParallax = useTransform(scrollY, [0, 1000], [0, -150]);
  const bgRotate = useTransform(scrollY, [0, 1000], [0, 10]);
  
  // Fetch real influencer & brand profile photos for the testimonials collage
  useEffect(() => {
    const fetchProfilePhotos = async () => {
      try {
        const [influencers, brands] = await Promise.all([
          userService.getAllInfluencers(),
          userService.getAllBrands(),
        ]);

        const photos = [];
        influencers.forEach(u => {
          const url = u.profileImageUrl || u.photoURL;
          if (url) photos.push(url);
        });
        brands.forEach(u => {
          const url = u.brandDetails?.logo || u.photoURL;
          if (url) photos.push(url);
        });

        // Shuffle and take up to 9 for the collage
        photos.sort(() => Math.random() - 0.5);
        setProfilePhotos(photos.slice(0, 9));
      } catch (error) {
        console.error('Error fetching profile photos:', error);
      }
    };

    fetchProfilePhotos();
  }, []);

  // Custom hook for revealing elements when they come into view
  const useReveal = () => {
    const controls = useAnimation();
    const [ref, inView] = useInView({
      threshold: 0.2,
      triggerOnce: true
    });
    
    useEffect(() => {
      if (inView) {
        controls.start('visible');
      }
    }, [controls, inView]);
    
    return [ref, controls];
  };
  

  // Platform statistics removed as requested

  const faqs = [
    {
      question: "How does the review process work?",
      answer: "Our platform focuses on authentic product reviews. Influencers receive products from brands, try them out, and share honest opinions with their audience. We encourage transparency in all content, including disclosure of partnerships."
    },
    {
      question: "What types of campaigns are available?",
      answer: "We offer various campaign types including product reviews, unboxing videos, sponsored content, brand ambassadorships, and more. Brands can specify their requirements, and influencers can choose campaigns that match their content style."
    },
    {
      question: "How is payment handled?",
      answer: "Payments are processed securely through our platform. Brands fund campaigns upfront, and influencers receive payment after content approval, typically within 7-14 days. Payment methods include bank transfer, GCash, and PayPal."
    },
    {
      question: "What makes Engaged Manila different?",
      answer: "Unlike other platforms, we specialize in authentic product reviews and focus on building long-term relationships between brands and influencers. Our verification process ensures quality on both sides, and our platform offers comprehensive analytics and support."
    }
  ];

  const testimonials = [
    {
      content: "I just tried the beta version and I'm impressed with how easy it is to navigate. The campaign setup process is intuitive, and I can't wait for the official launch!",
      author: {
        name: 'Anne Griffin',
        role: 'Fashion Influencer',
        imageUrl: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80',
      },
    },
    {
      content: "As a brand looking for authentic reviews, I'm excited about this platform's potential. The beta test showed me how streamlined the influencer selection process will be once it launches.",
      author: {
        name: 'Michael Garta',
        role: 'Marketing Director',
        imageUrl: 'https://images.unsplash.com/photo-1519244703995-f4e0f30006d5?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80',
      },
    },
  ];

  // Background elements for visual interest
  const BackgroundElements = () => {
    return (
      <div className="absolute inset-0 z-0 overflow-hidden pointer-events-none">
        {/* Top-right decorative element */}
        <motion.div 
          className="absolute top-20 right-[5%] w-64 h-64 rounded-full bg-primary opacity-5"
          style={{ y: bgParallax, rotate: bgRotate }}
          initial={{ scale: 0.8 }}
          animate={{ 
            scale: [0.8, 1, 0.8], 
            rotate: [0, 5, 0] 
          }}
          transition={{ 
            duration: 20, 
            repeat: Infinity,
            repeatType: "reverse" 
          }}
        />
        
        {/* Bottom-left decorative element */}
        <motion.div 
          className="absolute bottom-20 left-[5%] w-80 h-80 rounded-full bg-blue-400 opacity-5"
          style={{ y: useTransform(scrollY, [0, 1000], [0, 100]) }}
          initial={{ scale: 0.7 }}
          animate={{ 
            scale: [0.7, 0.9, 0.7], 
            rotate: [0, -5, 0] 
          }}
          transition={{ 
            duration: 15, 
            repeat: Infinity,
            repeatType: "reverse" 
          }}
        />
        
        {/* Middle-right floating icons */}
        <div className="absolute right-[10%] top-[40%] opacity-10">
          <motion.div 
            className="relative"
            initial={{ y: 0 }}
            animate={{ y: [-20, 20, -20] }}
            transition={{ duration: 8, repeat: Infinity }}
          >
            <motion.div 
              className="absolute -top-16 -left-16 p-4 bg-primary rounded-lg"
              animate={{ rotate: [0, 10, 0] }}
              transition={{ duration: 5, repeat: Infinity }}
            >
              <Star className="h-8 w-8 text-white" />
            </motion.div>
            <motion.div 
              className="absolute top-8 left-8 p-3 bg-blue-400 rounded-lg"
              animate={{ rotate: [0, -10, 0] }}
              transition={{ duration: 7, repeat: Infinity, delay: 1 }}
            >
              <Hash className="h-6 w-6 text-white" />
            </motion.div>
            <motion.div 
              className="absolute top-0 left-0 p-3 bg-indigo-500 rounded-lg"
              animate={{ rotate: [0, 15, 0] }}
              transition={{ duration: 6, repeat: Infinity, delay: 2 }}
            >
              <Camera className="h-6 w-6 text-white" />
            </motion.div>
          </motion.div>
        </div>
        
        {/* Middle-left floating icons */}
        <div className="absolute left-[10%] top-[60%] opacity-10">
          <motion.div 
            className="relative"
            initial={{ y: 0 }}
            animate={{ y: [20, -20, 20] }}
            transition={{ duration: 9, repeat: Infinity }}
          >
            <motion.div 
              className="absolute -top-16 -left-16 p-4 bg-green-500 rounded-lg"
              animate={{ rotate: [0, -8, 0] }}
              transition={{ duration: 6, repeat: Infinity }}
            >
              <ShoppingBag className="h-8 w-8 text-white" />
            </motion.div>
            <motion.div 
              className="absolute top-8 left-8 p-3 bg-primary rounded-lg"
              animate={{ rotate: [0, 12, 0] }}
              transition={{ duration: 8, repeat: Infinity, delay: 1.5 }}
            >
              <Smartphone className="h-6 w-6 text-white" />
            </motion.div>
          </motion.div>
        </div>
        
        {/* Subtle grid pattern overlay */}
        <div className="absolute inset-0 bg-grid-pattern opacity-[0.03] pointer-events-none"></div>
      </div>
    );
  };

  return (
    <div className="bg-white relative">
      <SEO
        title="Engaged Manila — Influencer Marketing & Product Review Platform"
        description="Connect brands with influencers for authentic product reviews. Browse campaigns, apply to collaborate, and grow your influence in the Philippines."
        url="/"
      />
      {/* Background decorative elements */}
      <BackgroundElements />
      {/* Using shared Header component */}
      <Header />
      {/* No need for spacer as Header component includes it */}

      {/* Hero Section — gradient hero with animated reveal */}
      <FluxCardHero />

      {/* Trusted Brands — auto-scrolling logo carousel */}
      <Logos3 heading="Trusted by Leading Brands" />

      {/* Platform Traction Section */}
      <TractionSection />

      {/* Hero — Transforming Influencer Marketing */}
      <Hero10
        title="Transforming Influencer Marketing"
        titleLine2Prefix="into"
        titleHighlight="Authentic Campaigns"
        description="Connect with genuine influencers and create meaningful partnerships that drive real results for your brand."
        images={[
          heroImage1,
          heroImage2,
          heroImage3,
        ]}
        imageAlts={['Influencer creating content', 'Brand collaboration meeting', 'Product review photoshoot']}
        animation="subtle"
      />

      {/* Features Section - Scroll Stacking Cards */}
      <ScrollCard />
      {/* Why Choose Us Section */}
      <WhyChooseUs />
      {/* How It Works Section */}
      <div id="how-it-works" className="bg-white relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            <h2 className="text-base text-primary font-semibold tracking-wide uppercase">How It Works</h2>
            <p className="mt-2 text-4xl leading-8 font-extrabold tracking-tight text-gray-900 sm:text-5xl">
              Simple process, powerful results
            </p>
            <p className="mt-4 max-w-2xl text-xl text-gray-500 mx-auto">
              From sign-up to your first rewarded review in four easy steps
            </p>
          </motion.div>
        </div>

        <HowItWorks
          features={[
            {
              title: "Create Profile",
              description: "Sign up and create your profile with your social media links and audience demographics.",
              colorTheme: "orange",
            },
            {
              title: "Browse Campaigns",
              description: "Discover review campaigns from top brands that match your niche and audience.",
              colorTheme: "blue",
            },
            {
              title: "Submit Reviews",
              description: "Create authentic reviews of products you receive from brands you love.",
              colorTheme: "purple",
            },
            {
              title: "Get Rewarded",
              description: "Receive payment or keep the products as compensation for your honest reviews.",
              colorTheme: "orange",
            },
          ]}
        />
      </div>
      {/* Testimonials - People Love Us */}
      <div className="bg-white py-16 md:py-24 relative overflow-hidden">
        <Integrations photos={profilePhotos} />
      </div>

      {/* FAQ Section */}
      <div className="bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-8"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <h2 className="text-base text-primary font-semibold tracking-wide uppercase">FAQ</h2>
            <p className="mt-2 text-4xl leading-8 font-extrabold tracking-tight text-gray-900 sm:text-5xl">
              Common Questions
            </p>
          </motion.div>
          <FAQScrollAccordion items={faqs} title={null} className="pt-0 pb-16 md:pb-24" />
        </div>
      </div>

      {/* CTA Section */}
      <motion.div 
        className="bg-primary relative overflow-hidden"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.7 }}
      >
        {/* CTA background elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {/* Animated circles */}
          <motion.div 
            className="absolute -right-20 -bottom-20 w-80 h-80 rounded-full bg-white opacity-10"
            animate={{ 
              scale: [1, 1.2, 1],
              x: [0, -20, 0],
              y: [0, -20, 0]
            }}
            transition={{
              duration: 15,
              repeat: Infinity,
              repeatType: "reverse"
            }}
          />
          <motion.div 
            className="absolute -left-10 top-10 w-40 h-40 rounded-full bg-blue-300 opacity-10"
            animate={{ 
              scale: [1, 1.3, 1],
              x: [0, 20, 0],
              y: [0, 20, 0]
            }}
            transition={{
              duration: 20,
              repeat: Infinity,
              repeatType: "reverse"
            }}
          />
          
          {/* Floating icons */}
          <div className="absolute inset-0 opacity-10 pointer-events-none">
            {[...Array(6)].map((_, i) => {
              const icons = [Star, Hash, Camera, ShoppingBag, Smartphone, MessagesSquare];
              const Icon = icons[i % icons.length];
              return (
                <motion.div
                  key={`cta-icon-${i}`}
                  className="absolute text-white"
                  style={{
                    left: `${Math.random() * 90 + 5}%`,
                    top: `${Math.random() * 80 + 10}%`
                  }}
                  animate={{
                    y: [0, Math.random() * 30 - 15],
                    rotate: [0, Math.random() * 40 - 20]
                  }}
                  transition={{
                    duration: Math.random() * 8 + 7,
                    repeat: Infinity,
                    repeatType: "reverse"
                  }}
                >
                  <Icon className="w-6 h-6" />
                </motion.div>
              );
            })}
          </div>
        </div>
        <div className="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:py-16 lg:px-8 lg:flex lg:items-center lg:justify-between relative z-10">
          <div className="text-3xl font-extrabold tracking-tight text-white sm:text-4xl">
            <div className="block">
              Ready to get started?
            </div>
            <div className="block text-blue-100">
              Join our platform today.
            </div>
          </div>
          <div className="mt-8 flex lg:mt-0 lg:flex-shrink-0">
            <a
              href="/register"
              className="inline-flex items-center justify-center px-5 py-3 border border-transparent text-base font-medium rounded-md text-primary bg-white hover:bg-blue-50 transition-colors duration-300 cursor-pointer shadow"
            >
              Sign up
            </a>
            <a
              href="/login"
              className="ml-3 inline-flex items-center justify-center px-5 py-3 border border-transparent text-base font-medium rounded-md text-white bg-primary-dark hover:bg-primary-dark transition-colors duration-300 cursor-pointer shadow"
            >
              Sign in
            </a>
          </div>
        </div>
      </motion.div>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default Home;

// Add this to your global CSS or in a style tag in your HTML
// .bg-grid-pattern {
//   background-image: linear-gradient(to right, rgba(59, 91, 165, 0.1) 1px, transparent 1px),
//                     linear-gradient(to bottom, rgba(59, 91, 165, 0.1) 1px, transparent 1px);
//   background-size: 20px 20px;
// }
